/**
 * Traduce la definición de un gráfico de Teams a opciones de ApexCharts.
 *
 * Los elementos `Chart.*` de Teams no tienen un esquema publicado tan estable
 * como el resto de la tarjeta, y según el generador los puntos llegan como
 * `{x, y}`, `{label, value}` o `{legend, value}`. El normalizador acepta las
 * tres formas en lugar de atarse a una.
 */

/** Punto ya normalizado, con la etiqueta del eje y su valor */
interface ChartPoint {
  x: string;
  y: number;
}

/** Serie ya normalizada, con su nombre de leyenda */
interface ChartSeries {
  name: string;
  points: ChartPoint[];
}

/** Colores con los que se pintan las series, el primero es el de la marca */
export interface ChartPalette {
  colors: string[];
  text: string;
  muted: string;
  border: string;
  surface: string;
  dark: boolean;
}

/**
 * Quita las claves que valen `undefined`.
 *
 * ApexCharts mezcla las opciones sobre sus defaults con su propio merge, y ese
 * merge copia los `undefined` tal cual: una clave en `undefined` no deja el
 * default, lo borra. Después la librería lee dentro de esa clave (por ejemplo
 * `config.fill.type`) y revienta, con el agravante de que el error sale por la
 * promesa de `render()`. Más barato podar acá que condicionar cada clave.
 *
 * @param {T} value Opciones a limpiar
 * @returns {T} Devuelve la misma estructura sin claves en undefined
 */
function prune<T>(value: T): T {
  if (Array.isArray(value)) return value.map(prune) as unknown as T;

  // Las funciones (los formatters) no se recorren, se pasan tal cual
  if (value && typeof value === 'object') {
    const clean: Record<string, any> = {};

    Object.entries(value as Record<string, any>).forEach(([key, item]) => {
      if (item === undefined) return;

      clean[key] = prune(item);
    });

    return clean as T;
  }

  return value;
}

/**
 * Abrevia una magnitud para que quepa en una etiqueta de eje. Las cifras del
 * agente son de millones, y sin esto el eje se llena de ceros.
 *
 * @param {number} value Valor a abreviar
 * @returns {string} Devuelve el valor con su sufijo, por ejemplo `18.9M`
 */
function compactNumber(value: number): string {
  const abs = Math.abs(value);

  if (abs >= 1e9) return `${(value / 1e9).toFixed(1)}B`;
  if (abs >= 1e6) return `${(value / 1e6).toFixed(1)}M`;
  if (abs >= 1e3) return `${(value / 1e3).toFixed(1)}K`;

  return `${value}`;
}

/**
 * Formatea una magnitud completa, con separador de miles, para el tooltip.
 *
 * @param {number} value Valor a formatear
 * @returns {string} Devuelve el valor completo
 */
function fullNumber(value: number): string {
  return Number(value).toLocaleString('en-US', { maximumFractionDigits: 2 });
}

/**
 * Normaliza un punto suelto, sin importar con qué nombres vino.
 *
 * @param {any} raw Punto tal como vino en el json
 * @param {number} index Posición, se usa como etiqueta de último recurso
 * @returns {ChartPoint} Devuelve el punto normalizado
 */
function readPoint(raw: any, index: number): ChartPoint {
  const label = raw?.x ?? raw?.label ?? raw?.legend ?? raw?.title ?? raw?.name ?? `${index + 1}`;
  const value = raw?.y ?? raw?.value ?? raw?.size ?? raw?.count ?? 0;

  return { x: String(label), y: Number(value) || 0 };
}

/**
 * Normaliza el bloque `data` de la definición a una lista de series.
 *
 * Cuando los items traen un array `values` dentro, cada item es una serie con
 * su leyenda; si no, todo `data` es una sola serie.
 *
 * @param {Record<string, any>} definition Definición cruda del gráfico
 * @returns {ChartSeries[]} Devuelve las series normalizadas
 */
function readSeries(definition: Record<string, any>): ChartSeries[] {
  const data = definition?.data;

  if (!Array.isArray(data) || data.length === 0) return [];

  const grouped = data.some(item => Array.isArray(item?.values) || Array.isArray(item?.data));

  if (grouped) {
    return data.map((item, index) => {
      const points = (item?.values ?? item?.data ?? []) as any[];

      return {
        name: String(item?.legend ?? item?.title ?? item?.name ?? `Serie ${index + 1}`),
        points: points.map(readPoint),
      };
    });
  }

  return [{
    name: String(definition?.title ?? definition?.yAxisTitle ?? ''),
    points: data.map(readPoint),
  }];
}

/**
 * Deduce el tipo de gráfico de ApexCharts a partir del nombre del elemento.
 *
 * @param {string} typeName Nombre del elemento, por ejemplo `Chart.Donut`
 * @returns Tipo de ApexCharts y si va apilado u horizontal
 */
function readKind(typeName: string): { type: string; horizontal: boolean; stacked: boolean } {
  const name = (typeName ?? '').toLowerCase();

  const stacked = name.includes('stacked');
  const horizontal = name.includes('horizontalbar');

  let type = 'bar';

  if (name.includes('donut')) type = 'donut';
  else if (name.includes('pie')) type = 'pie';
  else if (name.includes('gauge')) type = 'radialBar';
  else if (name.includes('area')) type = 'area';
  else if (name.includes('line')) type = 'line';

  return { type, horizontal, stacked };
}

/**
 * Arma las opciones de ApexCharts para una definición de gráfico de Teams.
 *
 * @param {Record<string, any>} definition Definición cruda, con su `type`
 * @param {ChartPalette} palette Colores y tema tomados del proyecto
 * @returns Opciones de ApexCharts, o null si la definición no trae datos
 */
export function buildChartOptions(definition: Record<string, any>, palette: ChartPalette): any {
  if (!definition) return null;

  const { type, horizontal, stacked } = readKind(definition.type);
  const series = readSeries(definition);

  // Un gauge no trae series: es un valor suelto contra un máximo
  if (type === 'radialBar') {
    const value = Number(definition.value ?? series[0]?.points[0]?.y ?? 0) || 0;
    const max = Number(definition.max ?? definition.maxValue ?? 100) || 100;

    return prune({
      ...baseOptions(type, definition, palette),
      series: [Math.round((value / max) * 100)],
      labels: [String(definition.title ?? '')],
      plotOptions: { radialBar: { hollow: { size: '58%' }, dataLabels: { name: { show: false }, value: { fontSize: '18px', color: palette.text } } } },
    });
  }

  if (series.length === 0) return null;

  // Pie y donut consumen números planos con sus etiquetas al lado
  if (type === 'pie' || type === 'donut') {
    const points = series[0].points;

    return prune({
      ...baseOptions(type, definition, palette),
      series: points.map(p => p.y),
      labels: points.map(p => p.x),
      legend: { show: definition.displayLegend !== false, position: 'bottom', labels: { colors: palette.muted } },
      dataLabels: { enabled: true },
      tooltip: { ...baseOptions(type, definition, palette).tooltip, y: { formatter: fullNumber } },
    });
  }

  // Las categorías del eje x salen de la serie más larga, para que ninguna
  // serie corta recorte el eje de las demás
  const categories = series
    .reduce((longest, current) => (current.points.length > longest.points.length ? current : longest), series[0])
    .points.map(p => p.x);

  return prune({
    ...baseOptions(type, definition, palette),
    series: series.map(s => ({ name: s.name, data: s.points.map(p => p.y) })),
    xaxis: {
      categories,
      title: definition.xAxisTitle ? { text: String(definition.xAxisTitle), style: { color: palette.muted } } : undefined,
      labels: { style: { colors: palette.muted } },
      axisBorder: { color: palette.border },
      axisTicks: { color: palette.border },
    },
    yaxis: {
      title: definition.yAxisTitle ? { text: String(definition.yAxisTitle), style: { color: palette.muted } } : undefined,
      labels: { style: { colors: palette.muted }, formatter: compactNumber },
    },
    plotOptions: { bar: { horizontal, borderRadius: 4, columnWidth: '55%' } },
    dataLabels: { enabled: definition.showBarValues === true },
    stroke: type === 'line' || type === 'area' ? { width: 2.5, curve: 'smooth' } : { width: 0 },
    fill: type === 'area' ? { type: 'gradient', gradient: { opacityFrom: 0.35, opacityTo: 0.05 } } : undefined,
    legend: { show: series.length > 1 && definition.displayLegend !== false, position: 'bottom', labels: { colors: palette.muted } },
    grid: { borderColor: palette.border, strokeDashArray: 4 },
    tooltip: { ...baseOptions(type, definition, palette).tooltip, y: { formatter: fullNumber } },
    ...(stacked ? { chart: { ...baseOptions(type, definition, palette).chart, stacked: true } } : {}),
  });
}

/**
 * Opciones comunes a todos los tipos: alto, tema, colores y título.
 *
 * @param {string} type Tipo de ApexCharts
 * @param {Record<string, any>} definition Definición cruda del gráfico
 * @param {ChartPalette} palette Colores y tema tomados del proyecto
 * @returns Bloque base de opciones
 */
function baseOptions(type: string, definition: Record<string, any>, palette: ChartPalette): any {
  return {
    chart: {
      type,
      height: Number(definition.height) || 260,
      width: '100%',
      fontFamily: 'inherit',
      foreColor: palette.muted,
      background: 'transparent',
      toolbar: { show: false },
      animations: { enabled: false },
    },
    theme: { mode: palette.dark ? 'dark' : 'light' },
    colors: palette.colors,
    title: definition.title
      ? { text: String(definition.title), style: { fontSize: '13px', fontWeight: 600, color: palette.text } }
      : undefined,
    tooltip: { theme: palette.dark ? 'dark' : 'light' },
  };
}
